import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { getApiUrl } from "../utils/baseUrl";
import Axios from "axios";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import LocalStorageService from "../core/services/local-storage.service";
import { trademoTrackingEvents } from "../core/global/trademo-tracking";
import { generateTrackingObject } from "../core/global/global-country-functions";
import trackingEventDictionary from "../core/dictionary/tracking-event";
import { captureException } from "@sentry/react";

const localStorageService = LocalStorageService.getService();
const apiEndpoints = {
  notifySales: getApiUrl("notifySales"),
};

export default function MultiSelect({ options, applyClicked, currentProduct, userPlan, pathname }) {
  const [showModal, setShowModal] = useState(false);
  const [selectedHsCodeList, setSelectedHsCodeList] = useState([]);
  const containerRef = useRef(null);

  const checkedOptions = useMemo(() => {
    return options.filter((item) => item.isChecked === true);
  }, [options]);

  const disabledButton = useMemo(() => {
    return options.every((option) => (option.isChecked === true && selectedHsCodeList.includes(option.key)) || (option.isChecked === false && !selectedHsCodeList.includes(option.key)));
  }, [options, selectedHsCodeList]);

  useEffect(() => {
    const tempArr = options.filter((item) => item.isChecked === true).map((item) => item.key);
    setSelectedHsCodeList(tempArr);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) {
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const callGetInTouch = useCallback(() => {
    setShowModal(false);
    const params = {
      planId: localStorageService && localStorageService.getUserCurrentPlan() ? localStorageService.getUserCurrentPlan() : "",
      countries: "Intel",
      key: "Intel Header Data Source",
    };
    Axios.get(apiEndpoints.notifySales, { params })
      .then((res) => {
        if (!!res && !!res.data && !!res.data.status && res.data.status.toUpperCase() === "success".toUpperCase()) {
          toast.success(res.data.message);
        } else {
          toast.error(res.data.message ? res.data.message : "Something went wrong. Please try again later !!!");
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error("Something went wrong. Please try again later !!!");
      });
  }, []);

  const handleSelectAll = useCallback(
    (checked) => {
      const tempArr = checked ? options.map((element) => element.key) : [];
      setSelectedHsCodeList(tempArr);
    },
    [options]
  );

  const handleItemToggle = useCallback((itemKey) => {
    setSelectedHsCodeList((prev) => {
      const newList = [...prev];
      const index = newList.indexOf(itemKey);
      if (index > -1) {
        newList.splice(index, 1);
      } else {
        newList.push(itemKey);
      }
      return newList;
    });
  }, []);

  const handleApplyChanges = useCallback(() => {
    applyClicked(selectedHsCodeList);
    setShowModal(false);
    trademoTrackingEvents(trackingEventDictionary.hsCodeBucketingSearchApplyChanges, generateTrackingObject(currentProduct, pathname, userPlan));
  }, [selectedHsCodeList, applyClicked, currentProduct, pathname, userPlan]);

  return (
    <div className="tw-flex tw-items-center tw-border tw-border-[#e7e7e7] tw-rounded-[48px] tw-gap-1 tw-px-3 tw-py-2 tw-shadow-autoSuggestPill" ref={containerRef}>
      <div className="tw-bg-white tw-py-1.5 tw-px-2 tw-cursor-pointer">
        <span className="box-text mr-2">Select HS Code:</span>
        {checkedOptions.slice(0, 3).map((item) => (
          <span key={item.key} className="tw-py-0.5 tw-px-2 tw-rounded-[14px] tw-bg-gray-100 tw-mr-1 tw-text-sm tw-font-medium tw-text-gray-900">
            {item.key}
          </span>
        ))}
        {checkedOptions.length > 3 && <span className="tw-py-0.5 tw-px-2 tw-rounded-[14px] tw-bg-gray-100 tw-mr-1 tw-text-sm tw-font-medium tw-text-gray-900">+ {checkedOptions.length - 3}</span>}
        <span
          className="float-right cursor-pointer arrow"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowModal(!showModal);
          }}>
          <img alt="dwon-arrow" src="https://res.cloudinary.com/drzta9shk/image/upload/v1684927719/global/chevron-down_uw3vfj.svg" />
        </span>
      </div>

      {showModal && (
        <div className="tw-absolute tw-bg-white tw-top-10 tw-mt-2 tw-w-[40%] tw-flex-grow-0 tw-py-2 tw-px-4 tw-rounded tw-shadow-datasetDropdown tw-border tw-border-gray-200">
          {options.length > 1 && (
            <>
              <span className="d-block mb-2">Please select from the following subscribed 4-digit HS Codes to view corresponding data</span>
              <span className="d-block tw-ml-5 py-2">
                <input type="checkbox" className="form-check-input after:-tw-ml-0.5" checked={options.length === selectedHsCodeList.length} onChange={(e) => handleSelectAll(e.target.checked)} />
                <span className="ml-2">Select All</span>
              </span>
            </>
          )}
          <div className="!tw-max-h-[200px] tw-overflow-scroll !tw-relative">
            {options.map((item) => {
              const description = item.description?.split("-")?.[1] || "";
              const isLongDescription = description.length > 50;
              const displayDescription = isLongDescription ? description.slice(0, 50) + "..." : description;
              return (
                <span className="d-block tw-ml-5 py-2" key={item.key}>
                  <input type="checkbox" className="form-check-input after:-tw-ml-0.5" checked={selectedHsCodeList.includes(item.key)} disabled={options.length === 1} onChange={() => handleItemToggle(item.key)} />
                  <span className="ml-2" data-tip={isLongDescription ? description : ""}>
                    {`HS Code ${item.key} : ${displayDescription}`}
                  </span>
                </span>
              );
            })}
          </div>
          {options.length > 1 && (
            <div className="tw-border-t tw-border-gray-200 tw-py-2 tw-w-full">
              <span data-tip={!selectedHsCodeList.length ? "You need to select at-least one HS Code" : ""}>
                <button className={!selectedHsCodeList.length || disabledButton ? "btn btn-primary trademo-new-btn disabled tw-w-fit tw-text-sm tw-rounded !tw-bg-[#e2d9fc] tw-text-white tw-border-none tw-h-9" : "btn btn-primary tw-w-fit trademo-new-btn tw-text-sm"} disabled={!selectedHsCodeList.length || disabledButton} onClick={handleApplyChanges}>
                  Apply Changes
                </button>
              </span>
              <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="right" />
            </div>
          )}
          {options.length < 20 && (
            <div className="tw-text-sm tw-text-gray-500 tw-border-t tw-border-gray-200 tw-py-4 tw-px-0">
              <span
                className="tw-text-[#f79009] tw-text-base tw-font-semibold tw-underline tw-cursor-pointer"
                onClick={() => {
                  callGetInTouch();
                  trademoTrackingEvents(trackingEventDictionary.hsCodeBucketingSearchUpsellSubscribe, generateTrackingObject(currentProduct, pathname, userPlan));
                }}>
                <img alt="thunder-icon" src="https://res.cloudinary.com/drzta9shk/image/upload/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg" height={24} />
                Get in Touch
              </span>
              <br />
              Get in touch with your Account Manager to subscribe to more HS Codes
            </div>
          )}
        </div>
      )}
    </div>
  );
}
